import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CN, GB, VN } from "country-flag-icons/react/3x2";
import { Check, ChevronDown, Languages } from "lucide-react";
import { LOCALES } from "@/i18n";
import { cn } from "@/lib/cn";

const FLAG_BY_LOCALE = {
  en: GB,
  vi: VN,
  "zh-Hans": CN,
} as const;

function LoginLanguageMenu() {
  const { i18n, t } = useTranslation();
  const selectedIndex = Math.max(0, LOCALES.findIndex((locale) => locale.code === i18n.language));
  const selected = LOCALES[selectedIndex]!;
  const SelectedFlag = FLAG_BY_LOCALE[selected.code];
  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(selectedIndex);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();

  useEffect(() => {
    if (open) itemRefs.current[focusIndex]?.focus();
  }, [open, focusIndex]);

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) setOpen(false);
    };
    const desktop = window.matchMedia("(min-width: 1024px)");
    const closeOnDesktop = () => { if (desktop.matches) setOpen(false); };
    document.addEventListener("pointerdown", closeOutside);
    desktop.addEventListener("change", closeOnDesktop);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      desktop.removeEventListener("change", closeOnDesktop);
    };
  }, [open]);

  function openMenu(index: number) {
    setFocusIndex(index);
    setOpen(true);
  }

  function closeMenu() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  return (
    <div
      ref={rootRef}
      className="relative shrink-0 lg:hidden"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-label={`${t("common.language")}: ${selected.name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => open ? closeMenu() : openMenu(selectedIndex)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            openMenu(event.key === "ArrowDown" ? 0 : LOCALES.length - 1);
          }
        }}
        className="inline-flex min-h-11 items-center gap-2 rounded-md border border-brand/25 bg-surface/90 px-3 text-[0.875rem] font-semibold text-brand-ink shadow-sm hover:bg-brand-tint focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        <SelectedFlag aria-hidden="true" focusable="false" className="h-4 w-6 shrink-0 rounded-xs shadow-sm ring-1 ring-line-strong/50" />
        <span aria-hidden="true">{selected.compactLabel}</span>
        <ChevronDown className={cn("size-4", open && "rotate-180")} aria-hidden="true" />
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={t("common.language")}
          className="absolute right-0 top-full z-50 mt-2 w-52 max-w-[calc(100vw-2.5rem)] rounded-lg border border-line-strong bg-surface p-1.5 shadow-[var(--shadow-pop)]"
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              closeMenu();
            } else if (event.key === "Tab") {
              // Start native tab navigation at the trigger, then close on blur.
              triggerRef.current?.focus();
              setOpen(false);
            } else if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
              event.preventDefault();
              setFocusIndex(event.key === "Home" ? 0 : event.key === "End" ? LOCALES.length - 1
                : (focusIndex + (event.key === "ArrowDown" ? 1 : -1) + LOCALES.length) % LOCALES.length);
            }
          }}
        >
          {LOCALES.map((locale, index) => {
            const Flag = FLAG_BY_LOCALE[locale.code];
            const active = index === selectedIndex;
            return (
              <button
                key={locale.code}
                ref={(element) => { itemRefs.current[index] = element; }}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                lang={locale.code}
                tabIndex={-1}
                onFocus={() => setFocusIndex(index)}
                onClick={() => {
                  void i18n.changeLanguage(locale.code);
                  closeMenu();
                }}
                className={cn(
                  "flex min-h-12 w-full items-center gap-3 rounded-md px-3 text-left text-[0.875rem] hover:bg-brand-tint focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand",
                  active ? "bg-brand-tint font-semibold text-brand-ink" : "text-ink-soft",
                )}
              >
                <Flag aria-hidden="true" focusable="false" className="h-4 w-6 shrink-0 rounded-xs shadow-sm ring-1 ring-line-strong/50" />
                <span className="flex-1">{locale.name}</span>
                {active && <Check className="size-4 shrink-0 text-brand" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function LanguageSwitch({
  variant = "segmented",
}: {
  variant?: "segmented" | "login";
}) {
  const { i18n, t } = useTranslation();
  const current = i18n.language;
  const login = variant === "login";

  return (
    <>
      {login && <LoginLanguageMenu />}
      <div
        className={cn(
          "items-center",
          login
            ? "hidden w-fit gap-1 rounded-lg border border-line-strong bg-surface/90 p-1 shadow-sm lg:flex"
            : "flex w-full rounded-sm border border-line bg-sunken/60 p-0.5",
        )}
        role="group"
        aria-label={t("common.language")}
      >
        {login && (
          <span className="mx-1 grid size-9 shrink-0 place-items-center border-r border-line pr-2 text-brand" aria-hidden="true">
            <Languages className="size-5" strokeWidth={1.75} />
          </span>
        )}
        {LOCALES.map((l) => {
          const active = current === l.code;
          const Flag = FLAG_BY_LOCALE[l.code];
          return (
            <button
              key={l.code}
              type="button"
              onClick={() => void i18n.changeLanguage(l.code)}
              aria-pressed={active}
              aria-label={login ? l.name : undefined}
              title={l.name}
              className={cn(
                "inline-flex min-w-7 items-center justify-center px-1.5 py-1.5 text-xs font-semibold leading-none transition-colors",
                login
                  ? cn(
                      "min-h-11 gap-2 rounded-md px-2.5 text-[0.8125rem] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none md:px-3",
                      active
                        ? "bg-brand text-brand-on shadow-sm"
                        : "text-ink-soft hover:bg-brand-tint hover:text-brand-ink",
                    )
                  : cn(
                      "flex-1 rounded-[3px]",
                      active
                        ? "bg-brand text-brand-on"
                        : "text-ink-faint hover:bg-sunken hover:text-ink-soft",
                    ),
              )}
            >
              {login ? (
                <Flag
                  aria-hidden="true"
                  focusable="false"
                  className="h-4 w-6 shrink-0 rounded-xs shadow-sm ring-1 ring-line-strong/50"
                />
              ) : null}
              <span>{login ? l.name : l.label}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}
